import { Link } from 'react-router-dom';
import '../styles/PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-page-wrapper">
      {/* Animated Background Glows */}
      <div className="privacy-bg-glow privacy-bg-glow-1"></div>
      <div className="privacy-bg-glow privacy-bg-glow-2"></div>

      <div className="privacy-container">
        {/* ============================= */}
        {/* HERO SECTION */}
        {/* ============================= */}
        <section className="privacy-hero-section">
          <div className="privacy-hero-content">
            <span className="privacy-hero-badge">🔒 Privacy Policy</span>
            <h1 className="privacy-hero-title">
              Your Privacy <span className="privacy-hero-highlight">Matters</span> to Us
            </h1>
            <p className="privacy-hero-description">
              At VernaEvents, we are committed to protecting your privacy and ensuring 
              the security of your personal information. This policy explains how we 
              collect, use, and safeguard your data.
            </p>
            <div className="privacy-hero-meta">
              <span className="privacy-hero-meta-item">📅 Last Updated: July 2026</span>
              <span className="privacy-hero-meta-item">📋 Version 2.0</span>
            </div>
          </div>
        </section>

        {/* ============================= */}
        {/* TABLE OF CONTENTS */}
        {/* ============================= */}
        <section className="privacy-toc-section">
          <div className="privacy-toc-content">
            <h2>Table of Contents</h2>
            <div className="privacy-toc-grid">
              <a href="#information-collect" className="privacy-toc-item">
                <span>1.</span> Information We Collect
              </a>
              <a href="#information-use" className="privacy-toc-item">
                <span>2.</span> How We Use Your Information
              </a>
              <a href="#information-share" className="privacy-toc-item">
                <span>3.</span> Information Sharing
              </a>
              <a href="#data-security" className="privacy-toc-item">
                <span>4.</span> Data Security
              </a>
              <a href="#cookies" className="privacy-toc-item">
                <span>5.</span> Cookies
              </a>
              <a href="#user-rights" className="privacy-toc-item">
                <span>6.</span> Your Rights
              </a>
              <a href="#children" className="privacy-toc-item">
                <span>7.</span> Children's Privacy
              </a>
              <a href="#changes" className="privacy-toc-item">
                <span>8.</span> Changes to This Policy
              </a>
              <a href="#contact" className="privacy-toc-item">
                <span>9.</span> Contact Us
              </a>
            </div>
          </div>
        </section>

        {/* ============================= */}
        {/* PRIVACY POLICY CONTENT */}
        {/* ============================= */}
        <div className="privacy-content-wrapper">
          {/* Section 1: Information We Collect */}
          <section id="information-collect" className="privacy-section">
            <div className="privacy-section-number">01</div>
            <div className="privacy-section-content">
              <h2>Information We Collect</h2>
              <p>
                When you use VernaEvents, we may collect the following types of information:
              </p>
              <div className="privacy-cards-grid">
                <div className="privacy-card">
                  <span className="privacy-card-icon">👤</span>
                  <h4>Personal Information</h4>
                  <ul>
                    <li>Name and contact details</li>
                    <li>Email address</li>
                    <li>Phone number</li>
                    <li>Team name and members</li>
                  </ul>
                </div>
                <div className="privacy-card">
                  <span className="privacy-card-icon">📊</span>
                  <h4>Usage Data</h4>
                  <ul>
                    <li>Pages visited</li>
                    <li>Events viewed</li>
                    <li>Search queries</li>
                    <li>Time spent on site</li>
                  </ul>
                </div>
                <div className="privacy-card">
                  <span className="privacy-card-icon">🍪</span>
                  <h4>Cookie Data</h4>
                  <ul>
                    <li>Session information</li>
                    <li>Preferences</li>
                    <li>Authentication tokens</li>
                    <li>Analytics data</li>
                  </ul>
                </div>
                <div className="privacy-card">
                  <span className="privacy-card-icon">📱</span>
                  <h4>Device Information</h4>
                  <ul>
                    <li>IP address</li>
                    <li>Browser type</li>
                    <li>Device type</li>
                    <li>Operating system</li>
                  </ul>
                </div>
              </div>
              <p className="privacy-section-note">
                <strong>Note:</strong> We only collect information that is necessary for 
                providing our services and improving your experience on VernaEvents.
              </p>
            </div>
          </section>

          {/* Section 2: How We Use Your Information */}
          <section id="information-use" className="privacy-section">
            <div className="privacy-section-number">02</div>
            <div className="privacy-section-content">
              <h2>How We Use Your Information</h2>
              <p>
                Your information helps us provide, maintain, and improve our services. 
                We use your data for the following purposes:
              </p>
              <ul className="privacy-list">
                <li>
                  <span className="privacy-list-icon">✅</span>
                  <div>
                    <strong>Event Registration</strong>
                    <p>Process your team registration and manage event participation</p>
                  </div>
                </li>
                <li>
                  <span className="privacy-list-icon">📧</span>
                  <div>
                    <strong>Communication</strong>
                    <p>Send confirmation emails, updates, and important notifications</p>
                  </div>
                </li>
                <li>
                  <span className="privacy-list-icon">🔍</span>
                  <div>
                    <strong>Service Improvement</strong>
                    <p>Analyze usage patterns to enhance user experience</p>
                  </div>
                </li>
                <li>
                  <span className="privacy-list-icon">🛡️</span>
                  <div>
                    <strong>Security</strong>
                    <p>Protect against fraud, abuse, and unauthorized access</p>
                  </div>
                </li>
                <li>
                  <span className="privacy-list-icon">📊</span>
                  <div>
                    <strong>Analytics</strong>
                    <p>Track engagement and improve our platform</p>
                  </div>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3: Information Sharing */}
          <section id="information-share" className="privacy-section">
            <div className="privacy-section-number">03</div>
            <div className="privacy-section-content">
              <h2>Information Sharing</h2>
              <p>
                We value your trust and do not sell or rent your personal information to third parties. 
                We may share information in the following cases:
              </p>
              <div className="privacy-sharing-grid">
                <div className="privacy-sharing-item">
                  <span className="privacy-sharing-icon">👥</span>
                  <div>
                    <h4>With Your Consent</h4>
                    <p>When you give us explicit permission to share your information</p>
                  </div>
                </div>
                <div className="privacy-sharing-item">
                  <span className="privacy-sharing-icon">⚖️</span>
                  <div>
                    <h4>Legal Requirements</h4>
                    <p>When required by law or to protect legal rights</p>
                  </div>
                </div>
                <div className="privacy-sharing-item">
                  <span className="privacy-sharing-icon">🤝</span>
                  <div>
                    <h4>Service Providers</h4>
                    <p>With trusted partners who help us operate our services</p>
                  </div>
                </div>
                <div className="privacy-sharing-item">
                  <span className="privacy-sharing-icon">🏢</span>
                  <div>
                    <h4>Business Transfers</h4>
                    <p>In connection with mergers, acquisitions, or asset sales</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Section 4: Data Security */}
          <section id="data-security" className="privacy-section">
            <div className="privacy-section-number">04</div>
            <div className="privacy-section-content">
              <h2>Data Security</h2>
              <p>
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <div className="privacy-security-grid">
                <div className="privacy-security-item">
                  <span className="privacy-security-icon">🔒</span>
                  <h4>Encryption</h4>
                  <p>All data transmitted is encrypted using SSL/TLS protocols</p>
                </div>
                <div className="privacy-security-item">
                  <span className="privacy-security-icon">🛡️</span>
                  <h4>Access Control</h4>
                  <p>Strict access controls and authentication measures</p>
                </div>
                <div className="privacy-security-item">
                  <span className="privacy-security-icon">📋</span>
                  <h4>Regular Audits</h4>
                  <p>Regular security audits and vulnerability assessments</p>
                </div>
                <div className="privacy-security-item">
                  <span className="privacy-security-icon">📦</span>
                  <h4>Data Backup</h4>
                  <p>Regular backups to prevent data loss</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5: Cookies */}
          <section id="cookies" className="privacy-section">
            <div className="privacy-section-number">05</div>
            <div className="privacy-section-content">
              <h2>Cookies</h2>
              <p>
                We use cookies to enhance your experience on VernaEvents. Cookies help us:
              </p>
              <ul className="privacy-list">
                <li>
                  <span className="privacy-list-icon">🍪</span>
                  <div>
                    <strong>Remember Your Preferences</strong>
                    <p>Save your settings for future visits</p>
                  </div>
                </li>
                <li>
                  <span className="privacy-list-icon">📊</span>
                  <div>
                    <strong>Analytics</strong>
                    <p>Understand how you use our platform</p>
                  </div>
                </li>
                <li>
                  <span className="privacy-list-icon">🔐</span>
                  <div>
                    <strong>Authentication</strong>
                    <p>Maintain your session securely</p>
                  </div>
                </li>
              </ul>
              <p className="privacy-section-note">
                <strong>You can control cookie preferences</strong> through your browser settings. 
                However, disabling cookies may affect some features of the platform.
              </p>
            </div>
          </section>

          {/* Section 6: Your Rights */}
          <section id="user-rights" className="privacy-section">
            <div className="privacy-section-number">06</div>
            <div className="privacy-section-content">
              <h2>Your Rights</h2>
              <p>You have the following rights regarding your personal information:</p>
              <div className="privacy-rights-grid">
                <div className="privacy-right-item">
                  <span className="privacy-right-icon">👁️</span>
                  <div>
                    <h4>Right to Access</h4>
                    <p>Request a copy of your personal data</p>
                  </div>
                </div>
                <div className="privacy-right-item">
                  <span className="privacy-right-icon">✏️</span>
                  <div>
                    <h4>Right to Rectification</h4>
                    <p>Request corrections to inaccurate data</p>
                  </div>
                </div>
                <div className="privacy-right-item">
                  <span className="privacy-right-icon">🗑️</span>
                  <div>
                    <h4>Right to Erasure</h4>
                    <p>Request deletion of your personal data</p>
                  </div>
                </div>
                <div className="privacy-right-item">
                  <span className="privacy-right-icon">⛔</span>
                  <div>
                    <h4>Right to Restrict</h4>
                    <p>Request restriction of data processing</p>
                  </div>
                </div>
                <div className="privacy-right-item">
                  <span className="privacy-right-icon">📤</span>
                  <div>
                    <h4>Right to Portability</h4>
                    <p>Receive your data in a portable format</p>
                  </div>
                </div>
                <div className="privacy-right-item">
                  <span className="privacy-right-icon">🚫</span>
                  <div>
                    <h4>Right to Object</h4>
                    <p>Object to data processing in certain cases</p>
                  </div>
                </div>
              </div>
              <p className="privacy-section-note">
                To exercise any of these rights, please <Link to="/contact">contact us</Link>.
              </p>
            </div>
          </section>

          {/* Section 7: Children's Privacy */}
          <section id="children" className="privacy-section">
            <div className="privacy-section-number">07</div>
            <div className="privacy-section-content">
              <h2>Children's Privacy</h2>
              <p>
                VernaEvents is not intended for children under 13 years of age. We do not knowingly 
                collect personal information from children under 13. If you believe we have collected 
                information from a child under 13, please <Link to="/contact">contact us</Link> 
                and we will take steps to remove the information.
              </p>
              <div className="privacy-children-note">
                <span>👶</span>
                <div>
                  <strong>Parental Consent Required</strong>
                  <p>Users under 18 require parental consent to use our platform</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 8: Changes to This Policy */}
          <section id="changes" className="privacy-section">
            <div className="privacy-section-number">08</div>
            <div className="privacy-section-content">
              <h2>Changes to This Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes 
                by posting the new policy on this page and updating the "Last Updated" date.
              </p>
              <div className="privacy-changes-timeline">
                <div className="privacy-change-item">
                  <span className="privacy-change-date">July 2026</span>
                  <span className="privacy-change-version">Version 2.0</span>
                  <p>Major update with GDPR compliance</p>
                </div>
                <div className="privacy-change-item">
                  <span className="privacy-change-date">January 2026</span>
                  <span className="privacy-change-version">Version 1.0</span>
                  <p>Initial privacy policy</p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 9: Contact Us - Updated with Footer Details */}
          <section id="contact" className="privacy-section">
            <div className="privacy-section-number">09</div>
            <div className="privacy-section-content">
              <h2>Contact Us</h2>
              <p>
                If you have any questions, concerns, or requests regarding this Privacy Policy, 
                please contact us:
              </p>
              <div className="privacy-contact-grid">
                <div className="privacy-contact-item">
                  <span className="privacy-contact-icon">📍</span>
                  <div>
                    <strong>Address</strong>
                    <p>VernaEvents, Justice Dwarakanath Rd, Gaza Park, Sreepally, Bhowanipore, Kolkata, West Bengal 700020</p>
                  </div>
                </div>
                <div className="privacy-contact-item">
                  <span className="privacy-contact-icon">📧</span>
                  <div>
                    <strong>Email</strong>
                    <p>info@vernaevents.com</p>
                  </div>
                </div>
                <div className="privacy-contact-item">
                  <span className="privacy-contact-icon">📱</span>
                  <div>
                    <strong>Phone</strong>
                    <p>+91 8017339796</p>
                  </div>
                </div>
                <div className="privacy-contact-item">
                  <span className="privacy-contact-icon">🕐</span>
                  <div>
                    <strong>Working Hours</strong>
                    <p>Mon - Sat: 9:00 AM - 6:00 PM</p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* ============================= */}
        {/* BOTTOM CTA */}
        {/* ============================= */}
        <section className="privacy-bottom-cta">
          <div className="privacy-bottom-content">
            <span className="privacy-bottom-icon">🔒</span>
            <div>
              <h4>Your Privacy is Our Priority</h4>
              <p>We are committed to protecting your personal information</p>
            </div>
            <Link to="/contact" className="privacy-bottom-btn">
              Contact Privacy Team →
            </Link>
          </div>
        </section>

        {/* ============================= */}
        {/* FOOTER NOTE */}
        {/* ============================= */}
       
      </div>
    </div>
  );
}

export default PrivacyPolicy;